import { useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { Logger } from '../support/Logger';
import { AuthorizedMediaUrlResolver } from './AuthorizedMediaUrlResolver';

// The url a media element should actually load. Every surface that renders an attachment or a
// member photo goes through this, so the two decisions below are made once rather than in each
// component:
//
// Nothing is rendered until the authorised url is ready, when one is coming. Seeding with the
// stored url instead would fetch the public bucket on every paint: unaudited, and a 403 followed by
// a flicker once the bucket is closed.
//
// On a CHANGE of media the blank arrives one frame late, not immediately: this runs in an effect,
// and effects run after paint, so React paints the previous url once under the new name before the
// reset lands. That matters because components here are reused across people — one avatar instance
// serves the care recipient switcher — so the alternative is the outgoing person's face sitting
// under the incoming person's name for as long as a resolve takes, rather than for a single frame.
//
// A resolution that lost a race is discarded. Resolving is an async hop, so when props change
// twice quickly the first call can settle last and leave the wrong image on screen.
//
// `fallbackWhenUnavailable` is used ONLY once a resolution has failed, never while one is still in
// flight, and the difference is the whole reason it is the hook that decides. A caller cannot tell
// those two apart from an undefined url, so it would have to fall back during both — and falling
// back during resolution puts a stand-in image on screen for as long as a token fetch takes,
// which for an avatar means a cartoon where a person's face is about to be.
export const useResolvedMediaUrl = (params: { url?: string; href?: string; fallbackWhenUnavailable?: string }): string | undefined => {
    const { url, href, fallbackWhenUnavailable } = params;
    const resolver = useInstance(AuthorizedMediaUrlResolver);
    const log = useInstance(Logger);

    const [resolved, setResolved] = useState<string | undefined>(href ? undefined : url);
    const latestKey = useRef<string>('');

    // Read through a ref so a change of fallback cannot restart the resolution. As a dependency it
    // would: a member picking a new avatar or colour changes the fallback while leaving the photo
    // untouched, and the effect would then blank the image and re-resolve a url that has not moved.
    // The ref is written on every render, so the catch below still sees the current value.
    const fallbackRef = useRef(fallbackWhenUnavailable);
    fallbackRef.current = fallbackWhenUnavailable;

    useAsyncEffect(async () => {
        const key = `${url ?? ''}|${href ?? ''}`;
        latestKey.current = key;

        if (!url) {
            setResolved(undefined);
            return;
        }

        setResolved(href ? undefined : url);

        // Caught rather than propagated for two reasons. useAsyncEffect rethrows into the nearest
        // error boundary, and this hook serves avatars scattered across a care map, so an
        // unhandled throw takes down the whole screen rather than one image — that exact bug
        // reached review once already. And the resolver now refuses to authorise a read it cannot
        // account for, which must degrade to something the caller chose, never to a silent
        // unaudited fetch of the public bucket: the fallback is a local asset, so it carries none
        // of the stored url's authority. Logged so a persistent failure is visible, not merely
        // quiet — a fallback that looks deliberate is exactly what hides a broken pipeline.
        try {
            const next = await resolver.resolve({ url, href });
            if (latestKey.current === key) {
                setResolved(next);
            }
        } catch (e) {
            log.error(e, { context: 'could not authorise a media read', href });
            if (latestKey.current === key) {
                setResolved(fallbackRef.current);
            }
        }
    }, [url, href]);

    return resolved;
};
